import { useCallback, useMemo, useState } from "react";

import { useStore } from "zustand/react";

import type { Order, OrderLeg, Product } from "~/v2/api/rest/account";
import { isOrderCancellable } from "~/v3-poc/features/orders/OrdersWidget.utils";
import { useAtdOrders } from "~/v3-poc/features/orders/useAtdOrders";
import type { WorkspaceStoreApi } from "../../shared/workspace/Workspace.context";
import type {
  ChartHistoricalOrderFill,
  ChartOpenLimitOrder,
  ChartOrderOverlay,
} from "./tradingView/TradingViewChart.types";

export interface UseChartOrderOverlaysParams {
  id: string;
  symbol: string;
  accountID: string | undefined;
  product: Product | undefined;
  store: WorkspaceStoreApi;
}

export interface ChartOrderOverlaysController {
  showOpenLimitOrders: boolean;
  showHistoricalOrderFills: boolean;
  openLimitOrders: ChartOpenLimitOrder[];
  historicalOrderFills: ChartHistoricalOrderFill[];
  selectedOrderOverlay: ChartOrderOverlay | undefined;
  onOpenLimitOrdersVisibleChange: (visible: boolean) => void;
  onHistoricalOrderFillsVisibleChange: (visible: boolean) => void;
  onOrderOverlaySelect: (overlay: ChartOrderOverlay | undefined) => void;
  onOrderDetailsDismiss: () => void;
}

const OPEN_LIMIT_ORDERS_POLLING_INTERVAL = 10_000;
const HISTORICAL_ORDER_FILLS_TIME_WINDOW = "last7Days" as const;

type ChartOrderSource = Order | OrderLeg;

const orderSourcesForSymbol = (
  orders: Order[],
  symbol: string,
): ChartOrderSource[] =>
  orders.flatMap((order) => {
    const matchingLegs =
      order.legs?.filter((leg) => leg.symbol === symbol) ?? [];
    return [...(order.symbol === symbol ? [order] : []), ...matchingLegs];
  });

const toOpenLimitOrders = (
  orders: Order[],
  symbol: string,
): ChartOpenLimitOrder[] =>
  orderSourcesForSymbol(orders, symbol).flatMap((order) => {
    const limitPrice = Number(order.limit_price);
    const placedAt = Date.parse(order.created_at);
    if (
      order.status === "held" ||
      !isOrderCancellable(order.status) ||
      order.order_type !== "limit" ||
      !Number.isFinite(limitPrice) ||
      !Number.isFinite(placedAt) ||
      !["buy", "sell", "sell_short"].includes(order.side)
    ) {
      return [];
    }
    return [
      {
        kind: "limit",
        id: order.id,
        limitPrice,
        placedAt: Math.floor(placedAt / 1000),
        quantity: order.qty ?? "",
        side: order.side as ChartOpenLimitOrder["side"],
      },
    ];
  });

const toHistoricalOrderFills = (
  orders: Order[],
  symbol: string,
): ChartHistoricalOrderFill[] =>
  orderSourcesForSymbol(orders, symbol).flatMap((order) => {
    const filledAt = order.filled_at ? Date.parse(order.filled_at) : Number.NaN;
    const price = Number(order.filled_avg_price);
    if (
      order.status !== "filled" ||
      !Number.isFinite(filledAt) ||
      !Number.isFinite(price) ||
      !["buy", "sell", "sell_short"].includes(order.side)
    ) {
      return [];
    }
    return [
      {
        kind: "fill",
        id: order.id,
        filledAt: Math.floor(filledAt / 1000),
        orderType: order.order_type,
        price,
        quantity: order.filled_qty,
        side: order.side as ChartHistoricalOrderFill["side"],
      },
    ];
  });

export const useChartOrderOverlays = ({
  id,
  symbol,
  accountID,
  product,
  store,
}: UseChartOrderOverlaysParams): ChartOrderOverlaysController => {
  const showOpenLimitOrders = useStore(store, (state) => {
    const settings = state.widgetSettings[id];
    return settings?.kind === "chart" && settings.showOpenLimitOrders === true;
  });
  const showHistoricalOrderFills = useStore(store, (state) => {
    const settings = state.widgetSettings[id];
    return (
      settings?.kind === "chart" && settings.showHistoricalOrderFills === true
    );
  });
  const [selectedOrderOverlay, setSelectedOrderOverlay] =
    useState<ChartOrderOverlay>();

  const { orders: openOrders } = useAtdOrders({
    accountID,
    product,
    pageSize: 499,
    symbols: [symbol],
    status: "open",
    orderType: "limit",
    enabled: showOpenLimitOrders,
    pollingInterval: showOpenLimitOrders
      ? OPEN_LIMIT_ORDERS_POLLING_INTERVAL
      : false,
  });
  const openLimitOrders = useMemo(
    () => (showOpenLimitOrders ? toOpenLimitOrders(openOrders, symbol) : []),
    [openOrders, showOpenLimitOrders, symbol],
  );
  const { orders: filledOrders } = useAtdOrders({
    accountID,
    product,
    pageSize: 499,
    symbols: [symbol],
    status: "filled",
    timeWindow: HISTORICAL_ORDER_FILLS_TIME_WINDOW,
    enabled: showHistoricalOrderFills,
    pollingInterval: showHistoricalOrderFills
      ? OPEN_LIMIT_ORDERS_POLLING_INTERVAL
      : false,
  });
  const historicalOrderFills = useMemo(
    () =>
      showHistoricalOrderFills
        ? toHistoricalOrderFills(filledOrders, symbol)
        : [],
    [filledOrders, showHistoricalOrderFills, symbol],
  );

  const onOpenLimitOrdersVisibleChange = useCallback(
    (visible: boolean) => {
      store.getState().setChartOpenLimitOrdersVisible(id, visible);
    },
    [id, store],
  );
  const onHistoricalOrderFillsVisibleChange = useCallback(
    (visible: boolean) => {
      store.getState().setChartHistoricalOrderFillsVisible(id, visible);
    },
    [id, store],
  );
  const onOrderOverlaySelect = useCallback(
    (overlay: ChartOrderOverlay | undefined) => {
      setSelectedOrderOverlay(overlay);
    },
    [],
  );
  const onOrderDetailsDismiss = useCallback(() => {
    setSelectedOrderOverlay(undefined);
  }, []);

  return {
    showOpenLimitOrders,
    showHistoricalOrderFills,
    openLimitOrders,
    historicalOrderFills,
    selectedOrderOverlay,
    onOpenLimitOrdersVisibleChange,
    onHistoricalOrderFillsVisibleChange,
    onOrderOverlaySelect,
    onOrderDetailsDismiss,
  };
};
